/**
 * Infinite scroll for the Featured In blog listing, matching viacitrus.com's
 * behavior: the next page of articles loads automatically as the sentinel at
 * the bottom of the grid nears the viewport (no click / URL change).
 *
 * Fetches the next page via Shopify's Section Rendering API (?section_id=...)
 * rather than a full navigation, then transplants the new cards + sentinel
 * out of the fetched markup -- keeps the request to just this section's HTML.
 */
class VcFeaturedIn extends HTMLElement {
  connectedCallback() {
    this.grid = this.querySelector('[data-vc-fi-grid]');
    this.sentinel = this.querySelector('[data-vc-fi-sentinel]');
    this.loading = false;

    if (!this.grid || !this.sentinel) return;

    // The sentinel is a real <a href> (working fallback if JS never runs, or the fetch below
    // fails) -- intercept a direct click so it also goes through the same AJAX path instead of a
    // full navigation, consistent with the auto-load-on-scroll behavior below.
    this.handleClick = (event) => {
      event.preventDefault();
      this.loadNext();
    };
    this.sentinel.addEventListener('click', this.handleClick);

    this.observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) this.loadNext();
      },
      { rootMargin: '600px 0px' }
    );
    this.observer.observe(this.sentinel);
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    if (this.handleClick) this.sentinel?.removeEventListener('click', this.handleClick);
  }

  async loadNext() {
    if (this.loading || !this.sentinel) return;

    const nextUrl = this.sentinel.dataset.nextUrl;
    const sectionId = this.sentinel.dataset.sectionId;
    if (!nextUrl || !sectionId) return;

    this.loading = true;
    this.sentinel.classList.add('vc-fi-sentinel--loading');

    try {
      const separator = nextUrl.includes('?') ? '&' : '?';
      const response = await fetch(`${nextUrl}${separator}section_id=${sectionId}`);
      if (!response.ok) throw new Error(`Featured In pagination fetch failed: ${response.status}`);

      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');

      const newGrid = doc.querySelector('[data-vc-fi-grid]');
      if (newGrid) {
        this.grid.append(...newGrid.children);
      }

      const newSentinel = doc.querySelector('[data-vc-fi-sentinel]');
      if (newSentinel) {
        this.sentinel.dataset.nextUrl = newSentinel.dataset.nextUrl;
        // Keep the real href fallback pointed at the correct next page too, not just the
        // JS-only dataset the fetch itself reads from.
        this.sentinel.href = newSentinel.getAttribute('href');
      } else {
        this.observer.unobserve(this.sentinel);
        this.sentinel.remove();
        this.sentinel = null;
      }
    } catch (error) {
      console.error(error);
      this.observer.unobserve(this.sentinel);
    } finally {
      this.loading = false;
      this.sentinel?.classList.remove('vc-fi-sentinel--loading');
    }
  }
}

if (!customElements.get('vc-featured-in')) {
  customElements.define('vc-featured-in', VcFeaturedIn);
}
